import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Products.css';
import productData, { getProductImageStyle, getProductDescriptionStyle } from '../data/productData';

const Products = () => {
  const [activeTab, setActiveTab] = useState('video');
  const productsRef = useRef([]);
  const firstRenderRef = useRef(true); // 用于标记是否是首次渲染

  // 检测设备类型
  const [deviceType, setDeviceType] = useState(window.innerWidth > 768 ? 'desktop' : 'mobile');

  // 处理标签切换
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // 重置引用数组
    productsRef.current = [];
    firstRenderRef.current = false; // 标记不再是首次渲染
  };

  // 监听产品项是否进入可视区域
  useEffect(() => {
    // 清理过期的引用
    productsRef.current = productsRef.current.slice(0, productData.products[activeTab].length);

    // 所有产品项先隐藏，等待动画触发
    setTimeout(() => {
      productsRef.current.forEach(item => {
        if (item) {
          item.classList.remove('animate-in');
          // 设置初始透明度为0
          item.style.opacity = '0';
        }
      });
    }, 0);

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1 // 当10%的产品项进入视口时触发
    };

    const observerCallback = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // 获取当前产品项的索引，用于计算延迟
          const index = productsRef.current.indexOf(entry.target);
          // 首次渲染时延迟更长，确保页面加载完成
          const baseDelay = firstRenderRef.current ? 300 : 100;
          const delay = index * 150; // 每个项目延迟150ms

          // 延迟添加动画类，确保DOM已更新并实现依次出现效果
          setTimeout(() => {
            if (entry.target && document.body.contains(entry.target)) {
              // 重置透明度并添加动画类
              entry.target.style.opacity = '1';
              entry.target.classList.add('animate-in');
            }
          }, baseDelay + delay);
          observer.unobserve(entry.target); // 动画触发后不再观察
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // 为每个产品项添加观察
    setTimeout(() => {
      productsRef.current.forEach(item => {
        if (item && document.body.contains(item)) {
          observer.observe(item);
        }
      });
    }, firstRenderRef.current ? 500 : 200); // 首次加载给更多时间

    // 如果URL中有锚点，在标签切换后处理滚动
    setTimeout(() => {
      const hash = window.location.hash.replace('#', '');
      if (hash && hash.includes('-')) {
        const [categoryId, productId] = hash.split('-');
        if (categoryId === activeTab) {
          const elementId = `product-${productId}`;
          const element = document.getElementById(elementId);
          if (element) {
            console.log(`标签切换后滚动到元素: #${elementId}`);
            element.scrollIntoView();
            window.scrollBy(0, -100);
          }
        }
      }
    }, 1000);

    return () => {
      observer.disconnect(); // 完全断开观察器连接
    };
  }, [activeTab]); // 当活动标签改变时重新设置观察者

  // 首次渲染后更新标记
  useEffect(() => {
    return () => {
      firstRenderRef.current = false;
    };
  }, []);

  // 处理URL锚点，滚动到对应位置
  useEffect(() => {
    const handleHash = () => {
      // 获取当前URL中的hash
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        console.log('处理URL锚点:', hash);

        // 如果是分类-产品ID格式 (如 'video-cloud-meeting')
        if (hash.includes('-')) {
          const [categoryId, productId] = hash.split('-');
          console.log(`解析锚点: 分类=${categoryId}, 产品ID=${productId}`);

          // 确保分类存在
          if (productData.categories.some(cat => cat.id === categoryId)) {
            // 先切换到正确的标签
            setActiveTab(categoryId);

            // 不在这里处理滚动，滚动会在标签切换和产品列表渲染完成后的useEffect中处理
            console.log(`已切换到分类: ${categoryId}，滚动将在渲染完成后处理`);
          }
        }
        // 如果只是分类ID (如 'video', 'live', 'education')
        else if (productData.categories.some(cat => cat.id === hash)) {
          console.log(`切换到分类标签: ${hash}`);
          setActiveTab(hash);
        }
        // 如果是product-前缀的产品ID (如 'product-cloud-meeting')
        else if (hash.startsWith('product-')) {
          const productId = hash.replace('product-', '');
          console.log(`处理产品ID: ${productId}`);

          // 查找该产品所属的分类
          let foundCategory = null;
          for (const [catId, products] of Object.entries(productData.products)) {
            if (products.some(p => p.id === productId)) {
              foundCategory = catId;
              break;
            }
          }

          if (foundCategory) {
            console.log(`产品所属分类: ${foundCategory}`);
            setActiveTab(foundCategory);
            // 滚动也会在标签切换后的useEffect中处理
          }
        }
      }
    };

    // 页面加载后处理hash
    handleHash();

    // 监听hashchange事件
    window.addEventListener('hashchange', handleHash);
    return () => {
      window.removeEventListener('hashchange', handleHash);
    };
  }, []);

  // 在产品列表渲染完成后，确保滚动到指定位置
  useEffect(() => {
    // 只有在所有产品项都设置了ref后才处理滚动
    if (productsRef.current.length === productData.products[activeTab].length) {
      const hash = window.location.hash.replace('#', '');

      // 如果是分类-产品ID格式
      if (hash && hash.includes('-')) {
        const [categoryId, productId] = hash.split('-');

        // 确保当前活动标签与锚点中的分类匹配
        if (categoryId === activeTab) {
          console.log(`产品列表渲染完成，准备滚动到 #product-${productId}`);

          // 使用requestAnimationFrame确保DOM已经完全渲染
          requestAnimationFrame(() => {
            const element = document.getElementById(`product-${productId}`);
            if (element) {
              // 强制在下一帧滚动，确保布局已经完成
              setTimeout(() => {
                console.log(`执行滚动到 #product-${productId}`);
                element.scrollIntoView({ behavior: 'smooth' });
                window.scrollBy(0, -100);
              }, 100);
            }
          });
        }
      }
    }
  }, [activeTab, productsRef.current.length]);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setDeviceType(window.innerWidth > 768 ? 'desktop' : 'mobile');
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 获取产品图片
  const getProductImage = (product) => {
    try {
      return require(`../assets/images/products/${product.image}`);
    } catch (e) {
      // 如果找不到图片，使用默认图片
      return require('../assets/images/products/default-product.svg');
    }
  };

  return (
    <div className="products-page">
      {/* 产品中心页面头部 */}
      <div className="page-header-products">
        <div className="header-bg-products">
          <div className="header-content-products">
            <h1>产品中心 <span className="en-title-products">Product center</span></h1>
            <div className="breadcrumb-products">
              <Link to="/">首页</Link> &gt; <Link to="/products">产品中心</Link> &gt; {productData.categories.find(category => category.id === activeTab)?.name}
            </div>
          </div>
        </div>
      </div>

      {/* 产品分类选项卡 */}
      <div className="product-tabs">
        <div className="tab-container-products">
          {productData.categories.map((category, index) => (
            <React.Fragment key={category.id}>
              {index > 0 && <div className="tab-divider"></div>}
              <div
                className={`tab ${activeTab === category.id ? 'active' : ''}`}
                onClick={() => handleTabChange(category.id)}
              >
                <span className={`tab-icon ${category.icon}`}></span>{category.name}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* 产品列表 */}
      <div className="products-container">
        {productData.categories.map(category => (
          <div
            key={category.id}
            className={`product-category ${activeTab === category.id ? 'slide-in' : ''}`}
            style={{ display: activeTab === category.id ? 'block' : 'none' }}
          >
            <h2>{category.name}</h2>
            <h3 className="en-category">{category.enName}</h3>
          </div>
        ))}

        {/* 产品列表项 */}
        <div className="product-list">
          {productData.products[activeTab].map((product, index) => {
             // 获取当前产品的样式
            const imageStyle = getProductImageStyle(product, deviceType);
            const descStyle = getProductDescriptionStyle(product, deviceType);

            return <div
              className={`product-item ${activeTab} ${product.id}`}
              key={product.id}
              id={`product-${product.id}`}
              ref={el => { productsRef.current[index] = el; }}
            >
              <Link to={`/products/${activeTab}/${product.id}`} className="product-item-content">
                <h3 className="product-details-title up">{product.name}</h3>
                <div
                  className="product-image"
                  style={{...imageStyle}}
                >
                  <div className="image-container">
                    <img src={getProductImage(product)} alt={product.name} />
                  </div>
                </div>
                <div
                  className="product-details"
                  style={{...descStyle}}
                >
                  <h3 className="product-details-title down">{product.name}</h3>
                  <p className="product-info">{product.description}</p>

                  {/* 显示特性图标 */}
                  {product.features && (
                    <div className="product-features">
                      {product.features.map((feature, idx) => (
                        <div className="feature-item" key={idx}>
                          <div className="feature-icon">
                            <div className="icon" style={{ backgroundImage: `url(${require(`../assets/icons/${feature.icon}.svg`)})` }}></div>
                          </div>
                          <div className="feature-text">{feature.text}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <span className="view-more">了解更多<svg className="arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg></span>
                </div>
              </Link>
            </div>
          })}
        </div>
      </div>
    </div>
  );
};

export default Products;